import { maxWaveformPeak, parseWaveformPeaks } from './waveformPeaks'

/**
 * Fetching one `waveform.dat` and reducing it to a single number, deduplicated
 * across every component that asks for it.
 *
 * The New Render dialog mounts around fifteen `ClipScene`s at once (thirteen
 * `<Thumbnail>`s plus the `<Player>`), each with its own visualization, each
 * needing the same whole-file peak. Without the module-level cache that is
 * fifteen identical 66 KB fetches on dialog open; with it, one.
 *
 * Pure and injectable (`fetch`, `cache`) so it is testable without a DOM —
 * `useWaveformPeak` is the React half.
 */

const defaultCache = new Map<string, Promise<number | null>>()

/**
 * Reads the whole-file peak, or `null` when there isn't one to be had.
 *
 * **Only successes stay cached.** A rejected fetch drops its entry on settle,
 * so the next mount retries. That covers a transient network error and a signed
 * URL that expired while a tab sat open past its 24 hours — *not* a waveform
 * generated after the page loaded, which is a common misreading of this cache:
 * the server only ever sends a `waveformUrl` for a file it has confirmed exists
 * (`getRecording.ts`, `resolveWaveformUrl`), so a not-yet-generated waveform
 * arrives as no URL at all and is discovered when the recording query refetches,
 * never by this loader.
 *
 * Never throws. A missing peak means the caller falls back to the window-local
 * scale, which is what the bars did before this existed.
 */
export function loadWaveformPeak(
  url: string | null | undefined,
  options: {
    fetch?: typeof globalThis.fetch
    cache?: Map<string, Promise<number | null>>
  } = {},
): Promise<number | null> {
  if (!url) return Promise.resolve(null)

  const cache = options.cache ?? defaultCache
  const cached = cache.get(url)
  if (cached) return cached

  const fetchFn = options.fetch ?? globalThis.fetch

  const promise = (async () => {
    const response = await fetchFn(url)
    if (!response.ok) {
      throw new Error(`Waveform fetch failed with status ${response.status}`)
    }
    const peak = maxWaveformPeak(
      parseWaveformPeaks(await response.arrayBuffer()),
    )
    // A file that parses to silence is a real answer, but not a usable
    // denominator — hand back `null` so the caller keeps the local scale, and
    // don't cache it as if it were a peak.
    return peak > 0 ? peak : null
  })()
    .catch((error) => {
      console.warn('Failed to load waveform peak:', error)
      return null
    })
    .then((peak) => {
      if (peak === null) cache.delete(url)
      return peak
    })

  cache.set(url, promise)

  return promise
}
